import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Alert, AlertDescription } from './ui/alert';
import { Save, Loader2, AlertTriangle } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Dialog d'édition des informations société d'un client (à partir d'une opération).
 *
 * Champs éditables :
 *   - companyName, rccm, compteContribuable (NCC), tvaStatus, address
 *
 * Backend : PATCH /api/users/{clientId}/company-info
 *   - Allowed: rccm, compteContribuable, address, tvaStatus, companyName
 *   - PROPAGATION AUTO à tous les comptes ayant la même companyName (gère doublons PAPS/NANO)
 */
export default function ClientInfoEditDialog({ open, onClose, operation, onSaved }) {
  const { toast } = useToast();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !operation) return;
    setForm({
      companyName: operation.companyName || '',
      rccm: operation.rccm || '',
      compteContribuable: operation.compteContribuable || '',
      tvaStatus: operation.tvaStatus || 'assujetti',
      address: operation.address || '',
    });
  }, [open, operation]);

  const clientId = operation?.clientId || operation?.userId;

  const handleChange = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!clientId) {
      toast({ title: 'Client introuvable', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.patch(`${API}/admin/users/${clientId}/company-info`, form);
      toast({
        title: 'Informations mises à jour',
        description: data?.message || 'Modifications enregistrées',
      });
      onSaved?.();
    } catch (err) {
      toast({
        title: 'Erreur',
        description: err?.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const nccLooksValid = !form.compteContribuable || /^\d{6,15}[A-Z]?$/i.test(form.compteContribuable.trim());

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-lg" data-testid="client-info-edit-dialog">
        <DialogHeader>
          <DialogTitle>Modifier les informations client</DialogTitle>
          <DialogDescription>
            Les modifications seront <strong>propagées automatiquement</strong> à tous les comptes
            avec le même nom d'entreprise (utile pour les équipes multi-utilisateurs).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label htmlFor="cie-edit-companyName" className="text-sm">Nom de l'entreprise</Label>
            <Input
              id="cie-edit-companyName"
              value={form.companyName ?? ''}
              onChange={(e) => handleChange('companyName', e.target.value)}
              placeholder="ex: NANO CI"
              data-testid="cie-input-companyName"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="cie-edit-rccm" className="text-sm">RCCM</Label>
              <Input
                id="cie-edit-rccm"
                value={form.rccm ?? ''}
                onChange={(e) => handleChange('rccm', e.target.value)}
                placeholder="CI-ABJ-..."
                className="font-mono text-sm"
                data-testid="cie-input-rccm"
              />
            </div>
            <div>
              <Label htmlFor="cie-edit-ncc" className="text-sm">
                NCC <span className="text-rose-600">*</span>
              </Label>
              <Input
                id="cie-edit-ncc"
                value={form.compteContribuable ?? ''}
                onChange={(e) => handleChange('compteContribuable', e.target.value)}
                placeholder="ex: 1234567A"
                className={`font-mono text-sm ${!nccLooksValid ? 'border-rose-500' : ''}`}
                data-testid="cie-input-ncc"
              />
              {!nccLooksValid && (
                <p className="text-[11px] text-rose-600 mt-0.5">
                  Format attendu : 6-15 chiffres, lettre optionnelle (ex: 1234567A)
                </p>
              )}
            </div>
          </div>

          <div>
            <Label htmlFor="cie-edit-tva" className="text-sm">Statut TVA</Label>
            <Select
              value={form.tvaStatus || 'assujetti'}
              onValueChange={(v) => handleChange('tvaStatus', v)}
            >
              <SelectTrigger id="cie-edit-tva" data-testid="cie-input-tvaStatus">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="assujetti">Assujetti (TVA 18%)</SelectItem>
                <SelectItem value="exo_conventionnelle">Exonération conventionnelle (TVAC)</SelectItem>
                <SelectItem value="exo_legale">Exonération légale (TVAD — export, TEE/RME)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="cie-edit-address" className="text-sm">Adresse</Label>
            <Input
              id="cie-edit-address"
              value={form.address ?? ''}
              onChange={(e) => handleChange('address', e.target.value)}
              placeholder="ex: Treichville, Abidjan, Côte d'Ivoire"
              data-testid="cie-input-address"
            />
          </div>

          {!form.compteContribuable && (
            <Alert className="border-amber-300 bg-amber-50">
              <AlertTriangle className="h-4 w-4 text-amber-700" />
              <AlertDescription className="text-xs">
                Sans NCC, la certification FNE sera émise en <strong>B2C</strong> (particulier)
                au lieu de B2B. À renseigner pour les vraies entreprises.
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Annuler
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || !nccLooksValid}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="cie-save-btn"
          >
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            <Save className="h-4 w-4 mr-2" />
            Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
